<template>
  <div>
    <!-- Botón para generar PDF profesional -->
    <v-btn
      color="success"
      class="ml-2"
      @click="generateProfessionalPDF"
      :loading="generatingPDF"
    >
      <v-icon left>mdi-file-pdf-box</v-icon>
      PDF
    </v-btn>

    <!-- Dialog de opciones de PDF -->
    <v-dialog v-model="pdfOptionsDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="headline">
          <v-icon left color="success">mdi-file-pdf-box</v-icon>
          Generar PDF Profesional
        </v-card-title>

        <v-card-text>
          <v-row>
            <v-col cols="12">
              <v-select
                v-model="pdfOptions.format"
                :items="formatOptions"
                label="Formato de página"
                outlined
                dense
              />
            </v-col>

            <v-col cols="12">
              <v-select
                v-model="pdfOptions.orientation"
                :items="orientationOptions"
                label="Orientación"
                outlined
                dense
              />
            </v-col>

            <v-col cols="12">
              <v-checkbox
                v-model="pdfOptions.includeCompanyLogo"
                label="Incluir logo de la empresa"
                hide-details
              />
            </v-col>

            <v-col cols="12">
              <v-text-field
                v-model="pdfOptions.fileName"
                label="Nombre del archivo"
                outlined
                dense
                suffix=".pdf"
              />
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="pdfOptionsDialog = false">Cancelar</v-btn>
          <v-btn color="success" @click="processPDF" :loading="generatingPDF">
            Generar PDF
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import jsPDF from "jspdf";
import "jspdf-autotable";
import QRCode from "qrcode";

export default {
  name: "ProfessionalPDFGenerator",
  props: {
    documentData: {
      type: Object,
      required: true,
    },
    visible: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      generatingPDF: false,
      pdfOptionsDialog: false,

      pdfOptions: {
        format: "a4",
        orientation: "portrait",
        includeCompanyLogo: true,
        fileName: "orden-compra",
      },

      formatOptions: [
        { text: "A4 (210 x 297 mm)", value: "a4" },
        { text: "Letter (216 x 279 mm)", value: "letter" },
        { text: "Legal (216 x 356 mm)", value: "legal" },
      ],

      orientationOptions: [
        { text: "Vertical (Portrait)", value: "portrait" },
        { text: "Horizontal (Landscape)", value: "landscape" },
      ],

      // Colores corporativos
      colors: {
        primary: "#1976D2",
        secondary: "#424242",
        accent: "#82B1FF",
        success: "#4CAF50",
        warning: "#FF9800",
        error: "#F44336",
        lightGray: "#F5F5F5",
        darkGray: "#616161",
      },
    };
  },

  computed: {
    isPurchaseOrder() {
      return this.documentData?.document?.doc_type === "OP";
    },
  },

  methods: {
    // Método para formatear moneda consistente
    formatCurrency(value) {
      const amount = parseFloat(value || 0);
      return amount.toLocaleString("es-ES", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    // Método para formatear números
    formatNumber(value, decimals = 2) {
      return parseFloat(value || 0).toFixed(decimals);
    },

    // Abrir dialog de opciones
    generateProfessionalPDF() {
      // Configurar nombre del archivo automáticamente
      const docType = this.documentData?.document?.doc_type || "DOC";
      const docNumber = this.documentData?.document?.doc_number || "SN";
      const date = new Date().toISOString().split("T")[0];
      this.pdfOptions.fileName = `${docType}-${docNumber}-${date}`;

      this.pdfOptionsDialog = true;
    },

    // Procesar generación del PDF
    async processPDF() {
      this.generatingPDF = true;

      try {
        await this.createProfessionalPDF();
        this.pdfOptionsDialog = false;

        this.$emit("pdf-generated", {
          success: true,
          fileName: this.pdfOptions.fileName,
        });
      } catch (error) {
        console.error("Error generando PDF:", error);
        this.$emit("pdf-error", error);
      } finally {
        this.generatingPDF = false;
      }
    },

    // Obtener nombre del tipo de documento
    getDocumentTypeName(docType) {
      const types = {
        TK: "TICKET",
        FS: "FACTURA SIMPLIFICADA",
        FC: "FACTURA COMPLETA",
        NC: "NOTA DE CRÉDITO",
        pos: "DOCUMENTO POS",
        OP: "ORDEN DE COMPRA",
        invoice: "FACTURA",
      };
      return types[docType] || "FACTURA COMPLETA";
    },

    // Crear PDF profesional
    async createProfessionalPDF() {
      const data = this.documentData;

      // Configurar jsPDF
      const pdf = new jsPDF({
        orientation: this.pdfOptions.orientation,
        unit: "mm",
        format: this.pdfOptions.format,
      });

      // Configurar fuentes
      pdf.setFont("helvetica");

      // Dimensiones de página
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10; // Cambiado a 1 centímetro
      const contentWidth = pageWidth - 2 * margin;

      let yPosition = margin;

      // === ENCABEZADO PROFESIONAL ===
      yPosition = await this.addProfessionalHeader(pdf, data, margin);

      // === INFORMACIÓN DEL PROVEEDOR ===
      yPosition = this.addSupplierInfo(pdf, data, margin, yPosition, contentWidth);

      // === TABLA DE PRODUCTOS ===
      await this.addProductsTable(pdf, data, margin, yPosition);

      // === QR, HUELLA Y TOTALES (al final de la página) ===
      if (data.document?.cufe) {
        await this.addQRHuellaAndTotals(pdf, data, margin, pageHeight, contentWidth);
      } else {
        await this.addTotalsOnly(pdf, data, margin, pageHeight, contentWidth);
      }

      // === PIE DE PÁGINA ===
      this.addFooter(pdf, data, pageHeight, contentWidth, margin);

      // Guardar PDF
      pdf.save(`${this.pdfOptions.fileName}.pdf`);
    },

    // Agregar encabezado profesional compacto
    async addProfessionalHeader(pdf, data, margin) {
      const company = data.company || {};
      const pageWidth = pdf.internal.pageSize.getWidth();

      // Encabezado más compacto - solo 40mm de altura
      pdf.setFillColor(25, 118, 210); // Color azul #1976D2
      pdf.rect(0, 0, pageWidth, 40, "F");

      // Configurar fuente monospace para mejor legibilidad
      pdf.setFont("courier", "bold");
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(18);

      // Layout en dos columnas: empresa (izq) y documento (der)
      const rightColumnStart = pageWidth * 0.65;

      // === COLUMNA IZQUIERDA: INFORMACIÓN DE LA EMPRESA ===
      pdf.text(company.PartyName || "C&M Software Developers Inc", margin, 15);

      pdf.setFont("courier", "normal");
      pdf.setFontSize(8);

      const companyInfo = [
        `NIF: ${company.PartyIdentification || "12133713"}`,
        `${company.AddressLine || "2121 Lake Shore Boulevard West"}`,
        `${company.CityName || "Madrid"}, ${company.Department || "13"}`,
        `Tel: ${company.PartyPhone || ""} | Email: ${company.PartyEmail || ""}`,
      ];

      let yPos = 22;
      companyInfo.forEach((line) => {
        if (line.trim() && (!line.includes(":") || line.split(":")[1]?.trim())) {
          pdf.text(line, margin, yPos);
          yPos += 3.5;
        }
      });

      // === COLUMNA DERECHA: INFORMACIÓN DEL DOCUMENTO ===
      pdf.setFont("courier", "bold");
      pdf.setFontSize(14);
      const documentTitle = this.getDocumentTypeName(data.document?.doc_type);
      pdf.text(documentTitle, rightColumnStart, 15);

      pdf.setFont("courier", "normal");
      pdf.setFontSize(10);
      const docPrefix = data.document?.doc_type === "TK" ? "TK-" : "";
      pdf.text(
        `Nº ${docPrefix}${data.document?.invoice || "123456789-1"}`,
        rightColumnStart,
        25
      );

      pdf.setFontSize(8);
      pdf.text(
        `Fecha: ${data.document?.issuedate || new Date().toLocaleDateString("es-ES")}`,
        rightColumnStart,
        32
      );

      // Restablecer color de texto
      pdf.setTextColor(0, 0, 0);
      pdf.setFont("helvetica", "normal");

      return 50; // Retornamos menos espacio
    },

    // Agregar información del proveedor compacta
    addSupplierInfo(pdf, data, margin, yPosition, contentWidth) {
      const buyer = data.buyer || {};

      // Sección más compacta del proveedor
      pdf.setFillColor(240, 240, 240); // Color gris más sutil
      pdf.rect(margin, yPosition, contentWidth, 30, "F");

      // Usar fuente monospace para mejor alineación
      pdf.setFont("courier", "bold");
      pdf.setFontSize(10);
      pdf.text("DATOS DEL CLIENTE", margin + 3, yPosition + 8);

      pdf.setFont("courier", "normal");
      pdf.setFontSize(8);

      const supplierInfo = [
        `Razón Social: ${buyer.PartyName || "PROVEEDOR DE PRUEBA 1"}`,
        `ID: ${buyer.PartyIdentification || "123456789"}`,
        `Dirección: ${buyer.AddressLine || "1030 King Street West"}`,
        `Email: ${buyer.PartyEmail || ""} | Tel: ${buyer.PartyPhone || ""}`,
      ];

      let infoY = yPosition + 15;
      supplierInfo.forEach((line) => {
        if (line.includes(":") && line.split(":")[1].trim()) {
          pdf.text(line, margin + 3, infoY);
          infoY += 4;
        }
      });

      return yPosition + 35;
    },

    // Agregar tabla de productos
    async addProductsTable(pdf, data, margin, yPosition) {
      const items = data.items || [];

      // Preparar datos de la tabla
      const tableData = [];

      items.forEach((item, index) => {
        // Calcular valores sin modificar los datos originales
        const cantidad = parseInt(item.item_quantity) || 1;
        const precioUnitario = parseFloat(item.item_cost || item.costo || 0);
        const impuestoUnitario = parseFloat(item.item_tax || 0);
        const descuentoUnitario = parseFloat(item.item_discount || 0);

        // Calcular totales de línea
        const subtotalBase = precioUnitario * cantidad;
        const totalImpuestos = impuestoUnitario * cantidad;
        const totalDescuentos = descuentoUnitario * cantidad;
        const totalLinea = subtotalBase + totalImpuestos - totalDescuentos;

        // Calcular tasa de impuesto tal como viene del sistema
        const tasaImpuesto =
          precioUnitario > 0 ? Math.round((impuestoUnitario / precioUnitario) * 100) : 0;

        // Nombre del producto
        const nombreProducto = item.item_concept || "Producto";
        // Solo poner el nombre del producto, el código se dibujará después
        const descripcionCompleta = nombreProducto;

        tableData.push([
          (index + 1).toString(),
          descripcionCompleta,
          cantidad.toString(),
          this.formatCurrency(precioUnitario),
          `${tasaImpuesto}%`,
          this.formatCurrency(totalLinea),
        ]);
      });

      // Configurar tabla con autoTable
      const tableOptions = {
        startY: yPosition,
        head: [["#", "Descripción", "Cant.", "Precio Unit.", "IVA", "Total"]],
        body: tableData,
        theme: "grid",
        headStyles: {
          fillColor: [25, 118, 210], // Color azul #1976D2
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 10,
        },
        bodyStyles: {
          fontSize: 9,
          cellPadding: 4,
          lineColor: [180, 180, 180], // Líneas grises
          lineWidth: 0.1, // Líneas mucho más finas
          minCellHeight: 15, // Altura mínima para que quepa el SKU/EAN
        },
        columnStyles: {
          0: {
            halign: "center",
            cellWidth: 12, // Reducido
            fontSize: 9,
          }, // #
          1: {
            halign: "left",
            cellWidth: 85, // Reducido
            fontSize: 9,
          }, // Descripción
          2: {
            halign: "center",
            cellWidth: 18, // Reducido
            fontSize: 9,
          }, // Cantidad
          3: {
            halign: "right",
            cellWidth: 25, // Reducido
            fontSize: 9,
          }, // Precio Unit.
          4: {
            halign: "center",
            cellWidth: 15, // Reducido
            fontSize: 9,
          }, // IVA
          5: {
            halign: "right",
            cellWidth: 28, // Reducido
            fontSize: 9,
          }, // Total
        },
        margin: { left: margin, right: margin },
        showHead: "everyPage",
        didParseCell: function (data) {
          // Guardar el índice del item para usar en didDrawCell
          if (data.column.index === 1 && data.section === "body") {
            // Asegurar que data.cell.raw sea un objeto
            if (typeof data.cell.raw === "string") {
              data.cell.raw = { text: data.cell.raw };
            }
            data.cell.raw.itemIndex = data.row.index;
          }
        },
        didDrawCell: function (data) {
          // Dibujar el código SKU/EAN debajo del nombre del producto
          if (
            data.column.index === 1 &&
            data.section === "body" &&
            data.cell.raw &&
            data.cell.raw.itemIndex !== undefined
          ) {
            const itemIndex = data.cell.raw.itemIndex;
            const item = items[itemIndex];
            const codigoProducto = item?.item_code;

            if (codigoProducto) {
              // Dibujar el código en letra más pequeña y gris
              pdf.setFontSize(6); // Reducido para que quepa mejor
              pdf.setTextColor(120, 120, 120); // Color gris más claro

              // Truncar el código si es muy largo
              const codigoTruncado =
                codigoProducto.length > 20
                  ? codigoProducto.substring(0, 20) + "..."
                  : codigoProducto;

              pdf.text(
                `SKU/EAN: ${codigoTruncado}`,
                data.cell.x + 2,
                data.cell.y + 12 // Posición fija desde el inicio de la celda
              );
              // Restaurar color y tamaño
              pdf.setFontSize(9);
              pdf.setTextColor(0, 0, 0);
            }
          }
        },
      };

      pdf.autoTable(tableOptions);

      return pdf.lastAutoTable.finalY + 10;
    },

    // Agregar QR, huella digital y totales en la misma línea al final de la página
    async addQRHuellaAndTotals(pdf, data, margin, pageHeight, contentWidth) {
      const cufe = data.document?.cufe;
      if (!cufe) return;

      // Posicionar al final de la página (50mm desde abajo para dejar espacio al pie)
      const yPosition = pageHeight - 60;

      try {
        // Configuración del layout: QR + Huella + Totales en la misma línea
        const qrSize = 30;
        const qrX = margin;
        const huellaX = margin + qrSize + 10;
        const huellaWidth = contentWidth * 0.4; // 40% del ancho para huella
        const totalsX = margin + qrSize + 10 + huellaWidth + 10;
        const totalsWidth = contentWidth - qrSize - 20 - huellaWidth;

        // Generar URL del QR
        const company = data.company || {};
        let qrUrl = "";
        if (company.environment === "1") {
          qrUrl = "https://catalogo-vpfe.dian.gov.co/document/searchqr?documentkey=";
        } else {
          qrUrl = "https://catalogo-vpfe-hab.dian.gov.co/document/searchqr?documentkey=";
        }
        qrUrl += cufe;

        // Generar y agregar QR
        const qrDataURL = await QRCode.toDataURL(qrUrl, {
          width: qrSize * 4,
          margin: 1,
          color: { dark: "#000000", light: "#FFFFFF" },
        });
        pdf.addImage(qrDataURL, "PNG", qrX, yPosition, qrSize, qrSize);

        // Texto del QR
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(6);
        pdf.setTextColor(80, 80, 80);
        pdf.text("Código QR", qrX, yPosition + qrSize + 4);
        pdf.text("verificación", qrX, yPosition + qrSize + 8);

        // Huella digital
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(8);
        pdf.setTextColor(60, 60, 60);
        pdf.text("Huella Digital:", huellaX, yPosition + 8);

        pdf.setFont("courier", "normal");
        pdf.setFontSize(6);
        const cufeLines = cufe.match(/.{1,35}/g) || [];
        let cufeY = yPosition + 15;
        cufeLines.forEach((line, index) => {
          if (index < 4) {
            pdf.text(line, huellaX, cufeY);
            cufeY += 4;
          }
        });

        // Totales
        this.addTotalsInSection(pdf, data, totalsX, yPosition, totalsWidth);

        // Restaurar color de texto
        pdf.setTextColor(0, 0, 0);
      } catch (error) {
        console.error("Error generando QR:", error);
      }
    },

    // Agregar solo totales cuando no hay CUFE
    async addTotalsOnly(pdf, data, margin, pageHeight, contentWidth) {
      const yPosition = pageHeight - 70;
      const totalsX = margin + contentWidth * 0.6;
      const totalsWidth = contentWidth * 0.4;

      this.addTotalsInSection(pdf, data, totalsX, yPosition, totalsWidth);
    },

    // Método auxiliar para agregar totales en una sección específica
    addTotalsInSection(pdf, data, totalsX, yPosition, totalsWidth) {
      const items = data.items || [];

      // Calcular totales
      let subtotal = 0;
      let impuestos = 0;
      let descuentos = 0;

      items.forEach((item) => {
        const cantidad = parseInt(item.item_quantity) || 1;
        const costoUnitario = parseFloat(item.item_cost || item.costo || 0);
        const impuestoUnitario = parseFloat(item.item_tax || 0);
        const descuentoUnitario = parseFloat(item.item_discount || 0) * cantidad;

        subtotal += costoUnitario * cantidad;
        impuestos += impuestoUnitario * cantidad;
        descuentos += descuentoUnitario;
      });

      const total = subtotal + impuestos - descuentos;

      // Fondo para totales
      pdf.setFillColor(250, 250, 250);
      pdf.rect(totalsX, yPosition, totalsWidth, 40, "F");

      // Título
      pdf.setFont("courier", "bold");
      pdf.setFontSize(10);
      pdf.text("RESUMEN", totalsX + 3, yPosition + 10);

      // Totales
      pdf.setFont("courier", "normal");
      pdf.setFontSize(8);

      const totalsData = [
        { label: "Subtotal:", value: this.formatCurrency(subtotal) },
        { label: "Impuestos:", value: this.formatCurrency(impuestos) },
        { label: "Descuentos:", value: this.formatCurrency(descuentos) },
      ];

      let totalsY = yPosition + 18;
      totalsData.forEach((item) => {
        pdf.text(item.label, totalsX + 3, totalsY);
        pdf.text(item.value, totalsX + totalsWidth - 3, totalsY, { align: "right" });
        totalsY += 3;
      });

      // Total final
      pdf.setFont("courier", "bold");
      pdf.setFontSize(10);
      pdf.text("TOTAL:", totalsX + 3, totalsY + 3);
      pdf.text(this.formatCurrency(total), totalsX + totalsWidth - 3, totalsY + 3, {
        align: "right",
      });
    },

    // Agregar pie de página
    addFooter(pdf, data, pageHeight, contentWidth, margin) {
      const totalPages = pdf.internal.getNumberOfPages();

      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);

        // Línea separadora
        pdf.setDrawColor(97, 97, 97); // Color gris oscuro #616161
        pdf.line(margin, pageHeight - 20, margin + contentWidth, pageHeight - 20);

        // Información del pie
        pdf.setFontSize(8);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(100, 100, 100);

        const documentTypeName = this.getDocumentTypeName(data.document?.doc_type);
        const documentNumber = data.document?.invoice || data.document?.doc_number || "";
        const footerLeft = `${documentTypeName} Nº ${documentNumber} - Generado el ${new Date().toLocaleDateString()}`;
        const footerRight = `Página ${i} de ${totalPages}`;

        pdf.text(footerLeft, margin, pageHeight - 10);
        pdf.text(footerRight, margin + contentWidth, pageHeight - 10, { align: "right" });
      }
    },
  },
};
</script>

<style scoped>
.v-btn {
  text-transform: none;
}
</style>
